import { useState } from "react";
import useSWRMutation from "swr/mutation";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import { ServicesURL } from "@utils/utilsApi";
import { buildActivityPayload } from "@hooks/hookscatalog/Activity/buildActivityPayload";
import { ActivityWizardData } from "../types";
import { mapWizardDataToInputForm } from "../payload/mapWizardDataToInputForm";

interface UseSubmitActivityV2Args {
  corporateId: string;
}

interface SubmitResult {
  loId: number;
  initiativesAssociated: number;
  topicsAssociated: number;
  categoriesAssociated: number;
}

interface UseSubmitActivityV2Return {
  submit: (data: ActivityWizardData) => Promise<SubmitResult>;
  isSubmitting: boolean;
  error: string | null;
}

export const useSubmitActivityV2 = ({
  corporateId,
}: UseSubmitActivityV2Args): UseSubmitActivityV2Return => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { trigger: triggerAddActivity } = useSWRMutation(
    ServicesURL.postActivities.replace(":idCorporate", corporateId),
    (url: string, { arg }: { arg: unknown }) =>
      fetcher(url, MethodType.POST, arg),
  );

  const { trigger: triggerInitiativeAssoc } = useSWRMutation(
    `${ServicesURL.catalogDomainAssociation}/${corporateId}/learning-objects/initiatives`,
    (
      _url: string,
      { arg }: { arg: { loId: number; initiativeId: number } },
    ) => {
      const realUrl = `${ServicesURL.catalogDomainAssociation}/${corporateId}/learning-objects/${arg.loId}/initiatives`;
      return fetcher(realUrl, MethodType.PUT, { id: arg.initiativeId });
    },
  );

  const { trigger: triggerTopicAssoc } = useSWRMutation(
    ServicesURL.catalogTopicAssociation,
    (
      _url: string,
      { arg }: { arg: { loId: number; initiativeId: number; topicId: number } },
    ) => {
      const realUrl = ServicesURL.catalogTopicAssociation
        .replace(":idCorporate", corporateId)
        .replace(":idLO", String(arg.loId))
        .replace(":initiativeId", String(arg.initiativeId));
      return fetcher(realUrl, MethodType.PUT, { id: arg.topicId });
    },
  );

  const { trigger: triggerCategoryAssoc } = useSWRMutation(
    ServicesURL.catalogCategoryAssociation,
    (
      _url: string,
      {
        arg,
      }: { arg: { loId: number; initiativeId: number; categoryId: number } },
    ) => {
      const realUrl = ServicesURL.catalogCategoryAssociation
        .replace(":idCorporate", corporateId)
        .replace(":idLO", String(arg.loId))
        .replace(":initiativeId", String(arg.initiativeId));
      return fetcher(realUrl, MethodType.PUT, { id: arg.categoryId });
    },
  );

  const submit = async (data: ActivityWizardData): Promise<SubmitResult> => {
    setIsSubmitting(true);
    setError(null);
    try {
      // Step 1: POST attività
      const inputForm = mapWizardDataToInputForm(data);
      const payload = buildActivityPayload(inputForm);
      const created = (await triggerAddActivity(payload)) as { id: number };
      const loId = created.id;

      // Step 2: PUT iniziative
      let initiativesAssociated = 0;
      for (const init of data.selected_initiatives) {
        await triggerInitiativeAssoc({
          loId,
          initiativeId: Number(init.id),
        });
        initiativesAssociated += 1;
      }

      // Step 3 + 4: PUT topics e categorie/caroselli per ogni iniziativa
      let topicsAssociated = 0;
      let categoriesAssociated = 0;
      const editorialInitiatives = data.is_standalone
        ? data.selected_initiatives
        : [];
      for (const init of editorialInitiatives) {
        const initiativeId = Number(init.id);
        const editorial = data.editorial_per_initiative[initiativeId] ?? {
          selected_carousel_ids: [],
          selected_topic_ids: [],
          selected_skill_ids: [],
        };

        for (const topicId of editorial.selected_topic_ids) {
          await triggerTopicAssoc({ loId, initiativeId, topicId });
          topicsAssociated += 1;
        }

        for (const categoryId of editorial.selected_carousel_ids) {
          await triggerCategoryAssoc({ loId, initiativeId, categoryId });
          categoriesAssociated += 1;
        }

        // Skill — placeholder: BE non pronto, skip per ora.
        // Quando il BE espone l'endpoint reale, aggiungere qui un loop su
        // `editorial.selected_skill_ids` come per topics/categorie.
      }

      return {
        loId,
        initiativesAssociated,
        topicsAssociated,
        categoriesAssociated,
      };
    } catch (e: any) {
      const msg = e?.message || "Submit attività fallito";
      setError(msg);
      throw e;
    } finally {
      setIsSubmitting(false);
    }
  };

  return { submit, isSubmitting, error };
};
